import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import type { LangueParlee } from '@sirocco/shared';
import { useEffect, useRef, useState, type TouchEvent } from 'react';
import { createPortal } from 'react-dom';
import { sensX, t } from '../../i18n';
import { ficheDe, LANGUES_MAX_PAR_SORTIE } from '../../utils/langues';
import { CatalogueDeLangues } from './CatalogueDeLangues';

/**
 * Les langues d'une sortie, choisies dans la liste de toutes.
 *
 * Cinq pastilles suffisaient a l'interface, pas au monde : une sortie a
 * Berlin se tient en allemand, une autre a Marrakech en darija. Le champ
 * montre ce qui est choisi, drapeau compris ; le toucher ouvre la liste
 * entiere, avec une recherche, et l'on coche ce qu'on y parlera. La
 * derniere cochee ne se decoche pas : une sortie sans langue ne dit rien
 * a personne.
 *
 * La liste se comporte comme un ecran de plus : elle entre par la droite,
 * et l'on revient en la tirant depuis le bord gauche, comme partout
 * ailleurs dans l'application (a l'envers en arabe).
 */
export function ChampDesLangues({
  valeur,
  onChange,
  label,
  onOuvrir,
}: {
  valeur: readonly LangueParlee[];
  onChange: (langues: LangueParlee[]) => void;
  label: string;
  /**
   * Ouvrir la liste soi-meme, dans sa propre fenetre.
   *
   * Pose par-dessus tout l'ecran, la liste passerait sous une feuille ou
   * la fermerait au premier appui : la feuille de modification la montre
   * donc a sa place, comme la fenetre des filtres.
   */
  onOuvrir?: () => void;
}) {
  const [ouvert, setOuvert] = useState(false);

  return (
    <div className="field field--spot">
      <span className="field__label">{label}</span>
      <button
        type="button"
        className="langues-champ"
        aria-haspopup="dialog"
        aria-label={`${label} : ${valeur.map((code) => ficheDe(code).nom).join(', ')}`}
        onClick={() => (onOuvrir ? onOuvrir() : setOuvert(true))}
      >
        <span className="langues-champ__choix">
          {valeur.map((code) => {
            const { nom, drapeau } = ficheDe(code);
            return (
              <span key={code} className="langues-champ__langue">
                <span className="drapeau" aria-hidden="true">
                  {drapeau}
                </span>
                <span lang={code}>{nom}</span>
              </span>
            );
          })}
        </span>
        <CaretRight size={16} weight="bold" className="sens-x langues-champ__fleche" />
      </button>
      {ouvert && (
        <ListeDesLangues
          titre={t.sorties.creation.languesTitre}
          valeur={valeur}
          onChange={onChange}
          onFermer={() => setOuvert(false)}
        />
      )}
    </div>
  );
}

/* Les memes seuils que le retour au doigt des ecrans : voir `useSwipeBack`. */
const BORD_PX = 30;
const ENGAGE_PX = 10;
const VALIDE = 0.3;
const POSE_MS = 260;

function ListeDesLangues({
  titre,
  valeur,
  onChange,
  onFermer,
}: {
  titre: string;
  valeur: readonly LangueParlee[];
  onChange: (langues: LangueParlee[]) => void;
  onFermer: () => void;
}) {
  /*
    Dans le cadre de l'application, par un portail : pose dans le
    formulaire, le panneau s'ancrerait a la zone qui defile et s'ouvrirait
    sous le pli, comme une feuille d'actions.
  */
  const [hote] = useState(
    () => document.querySelector<HTMLElement>('.app-frame') ?? document.body,
  );
  const panneau = useRef<HTMLDivElement>(null);
  const geste = useRef<{ x: number; y: number; engage: boolean; dx: number } | null>(null);

  /*
    Repartir en glissant vers la droite, comme un ecran qu'on quitte.

    Qui demande moins de mouvement n'en recoit aucun : la liste se ferme
    d'un coup.
  */
  function fermer() {
    const el = panneau.current;
    const calme = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!el || calme) {
      onFermer();
      return;
    }
    el.classList.remove('langues-liste--entree', 'langues-liste--tenue');
    el.classList.add('langues-liste--pose');
    el.style.transform = `translate3d(${100 * sensX}%, 0, 0)`;
    window.setTimeout(onFermer, POSE_MS);
  }

  /* La touche Echap referme, comme toute fenetre. */
  useEffect(() => {
    const surTouche = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onFermer();
    };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [onFermer]);

  /*
    Le geste s'arrete ici.

    La liste passe par un portail, mais React fait remonter ses evenements
    le long de l'arbre des composants, pas du document : un glissement
    depuis le bord arrivait donc aussi au formulaire de creation, dont le
    propre retour au doigt se declenchait en meme temps. On quittait la
    liste et le formulaire d'un seul geste, pour atterrir sur toutes les
    sorties. Ce que la liste touche reste a la liste.
  */
  /* Le doigt pose au bord d'ou l'on revient : a gauche, a droite en arabe. */
  function surDebut(event: TouchEvent<HTMLDivElement>) {
    event.stopPropagation();
    const doigt = event.touches[0];
    geste.current = null;
    if (event.touches.length !== 1 || !doigt) return;
    const bord = sensX === 1 ? doigt.clientX : window.innerWidth - doigt.clientX;
    if (bord > BORD_PX) return;
    geste.current = { x: doigt.clientX, y: doigt.clientY, engage: false, dx: 0 };
  }

  function surMouvement(event: TouchEvent<HTMLDivElement>) {
    event.stopPropagation();
    const suivi = geste.current;
    const doigt = event.touches[0];
    const el = panneau.current;
    if (!suivi || !doigt || !el) return;

    const dx = (doigt.clientX - suivi.x) * sensX;
    const dy = doigt.clientY - suivi.y;
    if (!suivi.engage) {
      // A rebours ou plutot vertical : c'est un defilement, pas un retour.
      if (dx <= 0 || Math.abs(dy) > Math.abs(dx)) {
        geste.current = null;
        return;
      }
      if (dx < ENGAGE_PX) return;
      suivi.engage = true;
      /* L'entree ne doit pas se rejouer quand on rend la main. */
      el.classList.remove('langues-liste--entree');
      el.classList.add('langues-liste--tenue');
    }
    suivi.dx = Math.max(0, dx);
    el.style.transform = `translate3d(${suivi.dx * sensX}px, 0, 0)`;
  }

  function surFin(event: TouchEvent<HTMLDivElement>) {
    event.stopPropagation();
    const suivi = geste.current;
    geste.current = null;
    const el = panneau.current;
    if (!suivi?.engage || !el) return;

    if (suivi.dx > el.clientWidth * VALIDE) {
      fermer();
      return;
    }
    // Pas assez loin : la liste revient a sa place.
    el.classList.remove('langues-liste--tenue');
    el.classList.add('langues-liste--pose');
    el.style.transform = '';
    window.setTimeout(() => el.classList.remove('langues-liste--pose'), POSE_MS);
  }

  return createPortal(
    <div
      ref={panneau}
      className="langues-liste langues-liste--entree"
      role="dialog"
      aria-modal="true"
      aria-label={titre}
      onAnimationEnd={(event) => {
        if (event.target === event.currentTarget) {
          event.currentTarget.classList.remove('langues-liste--entree');
        }
      }}
      onTouchStart={surDebut}
      onTouchMove={surMouvement}
      onTouchEnd={surFin}
      onTouchCancel={surFin}
    >
      <div className="langues-liste__tete">
        {/* La meme fleche que les autres ecrans : on sait qu'on peut revenir. */}
        <button type="button" className="sub-screen__back" aria-label={t.commun.retour} onClick={fermer}>
          <CaretLeft className="sens-x" size={22} weight="bold" />
        </button>
        <span className="langues-liste__titre">{titre}</span>
        <button type="button" className="langues-liste__ok" onClick={fermer}>
          {t.sorties.creation.languesTermine}
        </button>
      </div>
      <CatalogueDeLangues
        className="langues-liste__catalogue"
        valeur={valeur}
        onChange={onChange}
        auMoinsUne
        max={LANGUES_MAX_PAR_SORTIE}
      />
    </div>,
    hote,
  );
}
